import * as React from "react";
import {
  Ban,
  ChevronRight,
  ExternalLink,
  GitBranch,
  GitMerge,
  Link2,
  Link2Off,
  Pencil,
  Plus,
  RotateCcw,
  Shuffle,
  Unlink,
  UserPlus,
} from "lucide-react";

import { cn } from "@/lib/utils";
import type { WorkspaceNode } from "../../../types/workspace";
import {
  canOpenWorkspaceNodeDetail,
  workspaceChildActionsForNode,
  type WorkspaceChildActionKind,
  type WorkspaceChildAddMode,
} from "./workspaceChildActions";
import { WORKSPACE_NODE_TYPE_LABELS, workspaceNodeIsSimulationRerun, workspaceNodeTitle } from "./workspaceGraphUtils";

import type { BrokerAccountRef } from "@/utils/brokerAccountRef";

export type WorkspaceExecutingAccountActionsMenuProps = {
  label: string;
  mutating?: boolean;
  onOpenDetail?: () => void;
  onOpenAccountWorkspace?: (account: BrokerAccountRef) => void;
  onRemove?: () => void;
  account: BrokerAccountRef;
  onClose?: () => void;
};

/**
 * Executing-account canvas menu — same chrome as {@link WorkspaceNodeActionsMenu}.
 */
export function WorkspaceExecutingAccountActionsMenu({
  label,
  mutating = false,
  onOpenDetail,
  onOpenAccountWorkspace,
  onRemove,
  account,
  onClose,
}: WorkspaceExecutingAccountActionsMenuProps): React.ReactElement {
  const title = label.trim() || "Account";

  const closeAndRun = (action: () => void): void => {
    onClose?.();
    action();
  };

  return (
    <div
      data-workspace-node-actions=""
      data-slot="dropdown-menu-content"
      role="menu"
      className={cn(
        "nodrag nopan nowheel z-[1200] w-max overflow-visible rounded-[6px] bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10"
      )}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="px-2 py-1.5" data-workspace-node-actions-header="">
        <div className="flex flex-col gap-0.5">
          <span
            data-workspace-node-actions-type=""
            className="font-[family-name:var(--font-primary)] text-[10px] uppercase tracking-wide text-[color:var(--brand-tropical-surf)]"
          >
            Executing account
          </span>
          <span
            data-workspace-node-actions-title=""
            className="max-w-[16rem] truncate font-[family-name:var(--font-primary)] text-sm font-medium text-[#6b7280]"
            title={title}
          >
            {title}
          </span>
        </div>
      </div>
      <div className="-mx-1 my-1 h-px bg-border" />

      {onOpenDetail ? (
        <button
          type="button"
          role="menuitem"
          data-slot="dropdown-menu-item"
          disabled={mutating}
          className={cn(
            "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[#6b7280] outline-hidden select-none",
            "hover:bg-accent hover:text-[#6b7280]",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
          onClick={() => closeAndRun(() => onOpenDetail())}
        >
          <ExternalLink className="size-4 shrink-0 text-current" />
          <span className="whitespace-nowrap">Open detail</span>
        </button>
      ) : null}

      {onOpenAccountWorkspace ? (
        <button
          type="button"
          role="menuitem"
          data-slot="dropdown-menu-item"
          disabled={mutating}
          className={cn(
            "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[#6b7280] outline-hidden select-none",
            "hover:bg-accent hover:text-[#6b7280]",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
          onClick={() => closeAndRun(() => onOpenAccountWorkspace(account))}
        >
          <ExternalLink className="size-4 shrink-0 text-current" />
          <span className="whitespace-nowrap">Open account workspace</span>
        </button>
      ) : null}

      {onRemove ? (
        <>
          <div className="-mx-1 my-1 h-px bg-border" />
          <button
            type="button"
            role="menuitem"
            data-slot="dropdown-menu-item"
            data-variant="destructive"
            disabled={mutating}
            className={cn(
              "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none",
              "text-[color:var(--brand-glacial-red)] hover:bg-[color:var(--brand-glacial-red)]/10 hover:text-[color:var(--brand-glacial-red)]",
              "disabled:pointer-events-none disabled:opacity-50"
            )}
            onClick={() => closeAndRun(() => onRemove())}
          >
            <Link2Off className="size-4 shrink-0 text-current" />
            <span className="whitespace-nowrap">Remove…</span>
          </button>
        </>
      ) : null}
    </div>
  );
}

export type WorkspaceReparentCandidate = {
  node: WorkspaceNode;
  label: string;
};

export type WorkspaceNodeActionsMenuProps = {
  node: WorkspaceNode;
  nodeTitle?: string;
  accountLabel?: string;
  mutating?: boolean;
  canDisconnect?: boolean;
  /** Precomputed from canvas graph when meta is missing. */
  isSimulationRerun?: boolean;
  reparentCandidates?: WorkspaceReparentCandidate[];
  viewMode?: "account" | "strategy";
  onAddChild?: (
    node: WorkspaceNode,
    kind: WorkspaceChildActionKind,
    mode?: WorkspaceChildAddMode
  ) => void;
  onCombinePolicies?: (node: WorkspaceNode) => void;
  onRunCombinedSimulation?: (node: WorkspaceNode) => void;
  onRerunSimulation?: (node: WorkspaceNode) => void;
  /** Load this sim's policy tree into the editable simulator (creates a new sibling run). */
  onEditSimulationInSimulator?: (node: WorkspaceNode) => void;
  /** Open policy drawer focused on assigning executing accounts. */
  onAddExecutingAccount?: (node: WorkspaceNode) => void;
  onOpenDetail?: (node: WorkspaceNode) => void;
  /** Account canvas → Strategy federated view. */
  onOpenStrategyWorkspace?: (node: WorkspaceNode) => void;
  /** Strategy canvas → source account canvas deep link. */
  onOpenOnAccountCanvas?: (node: WorkspaceNode) => void;
  onToggleDisabled?: (node: WorkspaceNode) => void;
  onDisconnect?: (node: WorkspaceNode) => void;
  onReparent?: (node: WorkspaceNode, newParent: WorkspaceNode) => void;
  onBreakLink?: (node: WorkspaceNode) => void;
  onClose?: () => void;
};

/**
 * Inline under-node menu (not a portaled dropdown) so it stays glued to the
 * React Flow NodeToolbar and cannot drift when the viewport or pine dock moves.
 */
export function WorkspaceNodeActionsMenu({
  node,
  nodeTitle,
  accountLabel,
  mutating = false,
  canDisconnect = false,
  isSimulationRerun = false,
  reparentCandidates = [],
  viewMode = "account",
  onAddChild,
  onCombinePolicies,
  onRunCombinedSimulation,
  onRerunSimulation,
  onEditSimulationInSimulator,
  onAddExecutingAccount,
  onOpenDetail,
  onOpenStrategyWorkspace,
  onOpenOnAccountCanvas,
  onToggleDisabled,
  onDisconnect,
  onReparent,
  onBreakLink,
  onClose,
}: WorkspaceNodeActionsMenuProps): React.ReactElement {
  const [moveUnderOpen, setMoveUnderOpen] = React.useState(false);
  const typeLabel = WORKSPACE_NODE_TYPE_LABELS[node.type];
  const title = nodeTitle?.trim() || workspaceNodeTitle(node, { accountLabel });
  const isStrategyView = viewMode === "strategy";
  const childActions = workspaceChildActionsForNode(node, { viewMode });
  const canOpen = canOpenWorkspaceNodeDetail(node) && Boolean(onOpenDetail);
  const canOpenStrategy =
    !isStrategyView && node.type === "strategy" && Boolean(onOpenStrategyWorkspace);
  const canOpenOnAccount =
    isStrategyView &&
    Boolean(node.sourceWorkspaceId?.trim()) &&
    Boolean(onOpenOnAccountCanvas);
  const canBreakLink =
    !isStrategyView && node.type !== "account" && Boolean(onBreakLink);
  const canToggleDisabled =
    !isStrategyView && node.type !== "account" && Boolean(onToggleDisabled);
  const showDisconnect =
    !isStrategyView && canDisconnect && Boolean(onDisconnect);
  const canReparent =
    !isStrategyView &&
    node.type !== "account" &&
    Boolean(onReparent) &&
    reparentCandidates.length > 0;
  const canCombine =
    (node.type === "policy" || node.type === "combined_policy") &&
    Boolean(onCombinePolicies) &&
    !node.isDisabled;

  const canAddExecutingAccount =
    (node.type === "policy" || node.type === "combined_policy") &&
    Boolean(onAddExecutingAccount) &&
    !node.isDisabled;
  const canRunFromPolicy =
    !isStrategyView &&
    (node.type === "policy" || node.type === "combined_policy") &&
    Boolean(onRunCombinedSimulation) &&
    !node.isDisabled;
  // Only the original (master) simulation can spawn stacked date-window reruns.
  const isRerunNode = isSimulationRerun || workspaceNodeIsSimulationRerun(node);
  const canRerunSimulation =
    !isStrategyView &&
    (node.type === "simulation" || node.type === "combined_simulation") &&
    Boolean(onRerunSimulation) &&
    !node.isDisabled &&
    !isRerunNode;
  const canEditSimulationInSimulator =
    !isStrategyView &&
    (node.type === "simulation" || node.type === "combined_simulation") &&
    Boolean(onEditSimulationInSimulator) &&
    !node.isDisabled;
  const isPolicyFamily = node.type === "policy" || node.type === "combined_policy";

  const closeAndRun = (action: () => void): void => {
    onClose?.();
    action();
  };

  return (
    <div
      data-workspace-node-actions=""
      data-slot="dropdown-menu-content"
      role="menu"
      className={cn(
        "nodrag nopan nowheel z-[1200] w-max overflow-visible rounded-[6px] bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10"
      )}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="px-2 py-1.5" data-workspace-node-actions-header="">
        <div className="flex flex-col gap-0.5">
          <span
            data-workspace-node-actions-type=""
            className="font-[family-name:var(--font-primary)] text-[10px] uppercase tracking-wide text-[color:var(--brand-tropical-surf)]"
          >
            {typeLabel}
          </span>
          <span
            data-workspace-node-actions-title=""
            className="max-w-[16rem] truncate font-[family-name:var(--font-primary)] text-sm font-medium text-[#6b7280]"
            title={title}
          >
            {title}
          </span>
          {node.isDisabled ? (
            <span className="font-[family-name:var(--font-primary)] text-xs text-[#6b7280]">
              Disabled on canvas
            </span>
          ) : null}
        </div>
      </div>
      <div className="-mx-1 my-1 h-px bg-border" />

      {canOpen ? (
        <button
          type="button"
          role="menuitem"
          data-slot="dropdown-menu-item"
          disabled={mutating}
          className={cn(
            "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[#6b7280] outline-hidden select-none",
            "hover:bg-accent hover:text-[#6b7280]",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
          onClick={() => closeAndRun(() => onOpenDetail?.(node))}
        >
          <ExternalLink className="size-4 shrink-0 text-current" />
          <span className="whitespace-nowrap">Open detail</span>
        </button>
      ) : null}

      {isPolicyFamily ? (
        <>
          {canAddExecutingAccount ? (
            <button
              type="button"
              role="menuitem"
              data-slot="dropdown-menu-item"
              disabled={mutating}
              className={cn(
                "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[#6b7280] outline-hidden select-none",
                "hover:bg-accent hover:text-[#6b7280]",
                "disabled:pointer-events-none disabled:opacity-50"
              )}
              onClick={() => closeAndRun(() => onAddExecutingAccount?.(node))}
            >
              <UserPlus className="size-4 shrink-0 text-current" />
              <span className="whitespace-nowrap">Add an executing account</span>
            </button>
          ) : null}

          {canRunFromPolicy ? (
            <button
              type="button"
              role="menuitem"
              data-slot="dropdown-menu-item"
              disabled={mutating}
              className={cn(
                "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[#6b7280] outline-hidden select-none",
                "hover:bg-accent hover:text-[#6b7280]",
                "disabled:pointer-events-none disabled:opacity-50"
              )}
              onClick={() => closeAndRun(() => onRunCombinedSimulation?.(node))}
            >
              <Shuffle className="size-4 shrink-0 text-current" />
              <span className="whitespace-nowrap">Edit &amp; run simulation</span>
            </button>
          ) : null}

          {canCombine ? (
            <button
              type="button"
              role="menuitem"
              data-slot="dropdown-menu-item"
              disabled={mutating}
              className={cn(
                "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[#6b7280] outline-hidden select-none",
                "hover:bg-accent hover:text-[#6b7280]",
                "disabled:pointer-events-none disabled:opacity-50"
              )}
              onClick={() => closeAndRun(() => onCombinePolicies?.(node))}
            >
              <GitMerge className="size-4 shrink-0 text-current" />
              <span className="whitespace-nowrap">Combine policies…</span>
            </button>
          ) : null}

          <div className="-mx-1 my-1 h-px bg-border" />
        </>
      ) : null}

      {childActions.map((action) => (
        <button
          key={`${action.kind}:${action.mode}`}
          type="button"
          role="menuitem"
          data-slot="dropdown-menu-item"
          disabled={mutating || node.isDisabled || !onAddChild}
          className={cn(
            "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[#6b7280] outline-hidden select-none",
            "hover:bg-accent hover:text-[#6b7280]",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
          onClick={() => closeAndRun(() => onAddChild?.(node, action.kind, action.mode))}
        >
          {action.mode === "existing" ? (
            <Link2 className="size-4 shrink-0 text-current" />
          ) : (
            <Plus className="size-4 shrink-0 text-current" />
          )}
          <span className="whitespace-nowrap">{action.label}</span>
        </button>
      ))}

      {canCombine && !isPolicyFamily ? (
        <button
          type="button"
          role="menuitem"
          data-slot="dropdown-menu-item"
          disabled={mutating}
          className={cn(
            "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[#6b7280] outline-hidden select-none",
            "hover:bg-accent hover:text-[#6b7280]",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
          onClick={() => closeAndRun(() => onCombinePolicies?.(node))}
        >
          <GitMerge className="size-4 shrink-0 text-current" />
          <span className="whitespace-nowrap">Combine policies…</span>
        </button>
      ) : null}

      {canRunFromPolicy && !isPolicyFamily ? (
        <button
          type="button"
          role="menuitem"
          data-slot="dropdown-menu-item"
          disabled={mutating}
          className={cn(
            "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[#6b7280] outline-hidden select-none",
            "hover:bg-accent hover:text-[#6b7280]",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
          onClick={() => closeAndRun(() => onRunCombinedSimulation?.(node))}
        >
          <Shuffle className="size-4 shrink-0 text-current" />
          <span className="whitespace-nowrap">Edit &amp; run simulation</span>
        </button>
      ) : null}

      {canEditSimulationInSimulator ? (
        <button
          type="button"
          role="menuitem"
          data-slot="dropdown-menu-item"
          disabled={mutating}
          className={cn(
            "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[#6b7280] outline-hidden select-none",
            "hover:bg-accent hover:text-[#6b7280]",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
          onClick={() => closeAndRun(() => onEditSimulationInSimulator?.(node))}
        >
          <Pencil className="size-4 shrink-0 text-current" />
          <span className="whitespace-nowrap">Edit in simulator</span>
        </button>
      ) : null}

      {canRerunSimulation ? (
        <button
          type="button"
          role="menuitem"
          data-slot="dropdown-menu-item"
          disabled={mutating}
          className={cn(
            "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[#6b7280] outline-hidden select-none",
            "hover:bg-accent hover:text-[#6b7280]",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
          onClick={() => closeAndRun(() => onRerunSimulation?.(node))}
        >
          <RotateCcw className="size-4 shrink-0 text-current" />
          <span className="whitespace-nowrap">Rerun simulation…</span>
        </button>
      ) : null}

      {showDisconnect ? (
        <button
          type="button"
          role="menuitem"
          data-slot="dropdown-menu-item"
          disabled={mutating || node.isDisabled}
          className={cn(
            "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[#6b7280] outline-hidden select-none",
            "hover:bg-accent hover:text-[#6b7280]",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
          onClick={() => closeAndRun(() => onDisconnect?.(node))}
        >
          <Unlink className="size-4 shrink-0 text-current" />
          <span className="whitespace-nowrap">Disconnect</span>
        </button>
      ) : null}

      {canReparent ? (
        <>
          <button
            type="button"
            role="menuitem"
            data-slot="dropdown-menu-item"
            aria-expanded={moveUnderOpen}
            disabled={mutating || node.isDisabled}
            className={cn(
              "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[#6b7280] outline-hidden select-none",
              "hover:bg-accent hover:text-[#6b7280]",
              "disabled:pointer-events-none disabled:opacity-50"
            )}
            onClick={() => setMoveUnderOpen((open) => !open)}
          >
            <GitBranch className="size-4 shrink-0 text-current" />
            <span className="whitespace-nowrap">Move under…</span>
            <ChevronRight
              className={cn(
                "ml-auto size-4 shrink-0 text-current transition-transform",
                moveUnderOpen && "rotate-90"
              )}
              aria-hidden
            />
          </button>
          {moveUnderOpen
            ? reparentCandidates.map((candidate) => (
                <button
                  key={candidate.node.id}
                  type="button"
                  role="menuitem"
                  data-slot="dropdown-menu-item"
                  disabled={mutating || node.isDisabled}
                  className={cn(
                    "relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pl-8 pr-2 text-sm text-[#6b7280] outline-hidden select-none",
                    "hover:bg-accent hover:text-[#6b7280]",
                    "disabled:pointer-events-none disabled:opacity-50"
                  )}
                  onClick={() =>
                    closeAndRun(() => onReparent?.(node, candidate.node))
                  }
                >
                  <span className="min-w-0 flex-1 truncate text-left">
                    <span className="text-[color:var(--brand-tropical-surf)]">
                      {WORKSPACE_NODE_TYPE_LABELS[candidate.node.type]}
                      {" · "}
                    </span>
                    {candidate.label}
                  </span>
                </button>
              ))
            : null}
        </>
      ) : null}

      {canOpenStrategy ? (
        <button
          type="button"
          role="menuitem"
          data-slot="dropdown-menu-item"
          disabled={mutating}
          className={cn(
            "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[#6b7280] outline-hidden select-none",
            "hover:bg-accent hover:text-[#6b7280]",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
          onClick={() => closeAndRun(() => onOpenStrategyWorkspace?.(node))}
        >
          <GitBranch className="size-4 shrink-0 text-current" />
          <span className="whitespace-nowrap">Open strategy workspace</span>
        </button>
      ) : null}

      {canOpenOnAccount ? (
        <button
          type="button"
          role="menuitem"
          data-slot="dropdown-menu-item"
          disabled={mutating}
          className={cn(
            "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-[#6b7280] outline-hidden select-none",
            "hover:bg-accent hover:text-[#6b7280]",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
          onClick={() => closeAndRun(() => onOpenOnAccountCanvas?.(node))}
        >
          <ExternalLink className="size-4 shrink-0 text-current" />
          <span className="whitespace-nowrap">Open on account canvas</span>
        </button>
      ) : null}

      {canToggleDisabled ? (
        <button
          type="button"
          role="menuitem"
          data-slot="dropdown-menu-item"
          data-variant="warning"
          disabled={mutating}
          className={cn(
            "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none",
            "text-[color:var(--brand-comparison-period)] hover:bg-[color:var(--brand-comparison-period)]/10 hover:text-[color:var(--brand-comparison-period)]",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
          onClick={() => closeAndRun(() => onToggleDisabled?.(node))}
        >
          <Ban className="size-4 shrink-0 text-current" />
          <span className="whitespace-nowrap">
            {node.isDisabled ? "Enable node" : "Disable node"}
          </span>
        </button>
      ) : null}

      {canBreakLink ? (
        <button
          type="button"
          role="menuitem"
          data-slot="dropdown-menu-item"
          data-variant="destructive"
          disabled={mutating}
          className={cn(
            "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none",
            "text-[color:var(--brand-glacial-red)] hover:bg-[color:var(--brand-glacial-red)]/10 hover:text-[color:var(--brand-glacial-red)]",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
          onClick={() => closeAndRun(() => onBreakLink?.(node))}
        >
          <Link2Off className="size-4 shrink-0 text-current" />
          <span className="whitespace-nowrap">Remove…</span>
        </button>
      ) : null}
    </div>
  );
}
